import { Languages } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip } from "@/components/ui/tooltip"
import { type Digits, type Locale, useLocale, useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"

const choice = "flex h-8 w-full items-center gap-2 rounded-md px-2 text-sm text-muted-foreground hover:bg-raised hover:text-foreground aria-checked:bg-raised aria-checked:text-foreground"

/** Language and digits, saved for the signed-in user on this device (#3264 moves it to the cell). */
export function LocaleSwitcher({ className }: { className?: string }) {
  const t = useT()
  const { preference, setPreference } = useLocale()
  const locales: { id: Locale; label: string }[] = [
    { id: "en", label: t("locale.en") },
    { id: "ar", label: t("locale.ar") },
  ]
  const digits: { id: Digits; label: string }[] = [
    { id: "latn", label: t("locale.digitsLatn") },
    { id: "arab", label: t("locale.digitsArab") },
  ]
  return (
    <Popover>
      <Tooltip content={t("locale.label")}>
        <PopoverTrigger aria-label={t("locale.label")} className={className}>
          <Languages />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent side="top" align="end" className="w-56 p-1.5">
        <div role="radiogroup" aria-label={t("locale.label")}>
          {locales.map((l) => (
            <button key={l.id} type="button" role="radio" aria-checked={preference.locale === l.id} lang={l.id} className={choice} onClick={() => setPreference({ ...preference, locale: l.id })}>
              {l.label}
            </button>
          ))}
        </div>
        {preference.locale === "ar" ? (
          <div role="radiogroup" aria-label={t("locale.digits")} className="mt-1.5 border-t border-border-subtle pt-1.5">
            <p className="px-2 pb-1 text-2xs text-subtle-foreground">{t("locale.digits")}</p>
            {digits.map((d) => (
              <button key={d.id} type="button" role="radio" aria-checked={preference.digits === d.id} className={cn(choice, "font-mono tabular")} onClick={() => setPreference({ ...preference, digits: d.id })}>
                {d.label}
              </button>
            ))}
          </div>
        ) : null}
        <p className="px-2 pt-1.5 pb-0.5 text-2xs text-subtle-foreground">{t("locale.savedHere")}</p>
      </PopoverContent>
    </Popover>
  )
}
